import * as React from "react"
import { Input } from "@/shared/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select"
import { cn } from "@/lib/utils"

const countryCodes = [
  { code: "+1", country: "US/CA", flag: "🇺🇸" },
  { code: "+44", country: "UK", flag: "🇬🇧" },
  { code: "+33", country: "FR", flag: "🇫🇷" },
  { code: "+49", country: "DE", flag: "🇩🇪" },
  { code: "+91", country: "IN", flag: "🇮🇳" },
  { code: "+86", country: "CN", flag: "🇨🇳" },
  { code: "+81", country: "JP", flag: "🇯🇵" },
  { code: "+61", country: "AU", flag: "🇦🇺" },
  { code: "+55", country: "BR", flag: "🇧🇷" },
  { code: "+52", country: "MX", flag: "🇲🇽" },
  { code: "+34", country: "ES", flag: "🇪🇸" },
  { code: "+39", country: "IT", flag: "🇮🇹" },
  { code: "+7", country: "RU", flag: "🇷🇺" },
  { code: "+82", country: "KR", flag: "🇰🇷" },
]

interface PhoneInputProps extends Omit<React.ComponentProps<"input">, "onChange"> {
  value?: string
  onChange?: (value: string) => void
  countryCode?: string
  onCountryCodeChange?: (code: string) => void
}

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ className, value = "", onChange, countryCode = "+1", onCountryCodeChange, ...props }, ref) => {
    // Extract the phone number part (without country code)
    const phoneNumber = React.useMemo(() => {
      if (!value) return ""
      // Remove the current country code from the value if it exists
      const cleanValue = value.replace(/^\+\d+\s*/, "")
      return cleanValue
    }, [value])

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const phoneValue = e.target.value
      const fullValue = `${countryCode} ${phoneValue}`.trim()
      onChange?.(fullValue)
    }

    const handleCountryCodeChange = (newCode: string) => {
      onCountryCodeChange?.(newCode)
      const fullValue = `${newCode} ${phoneNumber}`.trim()
      onChange?.(fullValue)
    }

    return (
      <div className="flex w-full">
        <Select value={countryCode} onValueChange={handleCountryCodeChange}>
          <SelectTrigger className="w-[80px] rounded-r-none border-r-0 bg-background/50 flex-shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-background border shadow-lg z-50">
            {countryCodes.map((country) => (
              <SelectItem key={country.code} value={country.code}>
                <span className="flex items-center gap-2">
                  <span>{country.flag}</span>
                  <span className="text-xs">{country.code}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          {...props}
          ref={ref}
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneChange}
          className={cn("rounded-l-none flex-1", className)}
          spellCheck={false}
          autoCorrect="off"
        />
      </div>
    )
  }
)
PhoneInput.displayName = "PhoneInput"

export { PhoneInput }